<template>
  <transition name="chart-filter-slide">
    <div v-if="filterKeys.length > 0 || showQuestionPanel" class="chart-filter-panel">

      <!-- ═══ Normal filters ═══ -->
      <div
        v-for="filter in visibleFilterKeys"
        :key="filter"
        class="chart-filter-panel__item"
      >
        <label class="chart-filter-panel__label">{{ translateFilter(filter) }}</label>

        <!-- Date picker (FlatPickr) for date type filters -->
        <div
          v-if="getFilterType(filter) === 'date'"
          class="chart-filter-panel__datepicker-wrap"
        >
          <flat-pickr
            :value="activeFilters[filter] || ''"
            @on-change="
              (_, dateStr) => $emit('filter-change', { key: filter, value: dateStr })
            "
            :config="flatpickrConfig"
            :disabled="isFilterLocked(filter)"
            class="form-control chart-filter-panel__datepicker"
            :placeholder="translateFilter(filter)"
          />
          <button
            v-if="activeFilters[filter]"
            class="chart-filter-panel__datepicker-clear"
            type="button"
            @click="$emit('filter-change', { key: filter, value: null })"
          >
            ×
          </button>
        </div>

        <!-- Store-backed or static options (multi-select) -->
        <v-select
          v-else-if="getFilterType(filter) !== 'freetext'"
          :value="parseFilterValue(filter)"
          @input="(val) => onSelectInput(filter, val)"
          :options="getDisplayOptions(filter)"
          :reduce="(opt) => getReducedValue(filter, opt)"
          :loading="isFilterLoading(filter)"
          :clearable="!isFilterLocked(filter)"
          :disabled="isFilterLocked(filter)"
          :placeholder="translateFilter(filter)"
          label="label"
          multiple
          class="chart-filter-panel__select"
          append-to-body
          :calculate-position="withPopper"
        />

        <!-- Freetext / taggable fallback -->
        <v-select
          v-else
          :value="activeFilters[filter]"
          @input="(val) => $emit('filter-change', { key: filter, value: val })"
          :options="[]"
          taggable
          :clearable="!isFilterLocked(filter)"
          :disabled="isFilterLocked(filter)"
          :placeholder="translateFilter(filter)"
          class="chart-filter-panel__select"
          append-to-body
          :calculate-position="withPopper"
        />
      </div>

      <div v-if="showQuestionPanel" class="chart-filter-panel__analysis">
        <div class="chart-filter-panel__analysis-body" style="margin-top: 0">
          <!-- Loading indicator -->
          <div
            v-if="activeGroup && activeGroup.loading"
            class="chart-filter-panel__tab-loading"
          >
            ··· cargando preguntas...
          </div>

          <!-- Questions — rendered directly, no tabs -->
          <div
            v-if="activeGroup && activeGroup.questions.length > 0"
            class="chart-filter-panel__questions-body"
          >
            <div
              v-for="q in activeGroup.questions"
              :key="q.value"
              class="chart-filter-panel__item"
            >
              <div class="chart-filter-panel__question-header">
                <label
                  class="chart-filter-panel__label chart-filter-panel__label--question"
                  :title="q.value"
                >
                  {{ q.value }}
                </label>
                <button
                  type="button"
                  class="chart-filter-panel__graph-btn"
                  :class="{ 'is-active': isQuestionSelected(q.value) }"
                  :title="
                    isQuestionSelected(q.value)
                      ? 'Quitar de la gráfica'
                      : 'Graficar esta pregunta'
                  "
                  @click="$emit('toggle-selected-question', q.value)"
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="12"
                    height="12"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <line x1="18" y1="20" x2="18" y2="10" />
                    <line x1="12" y1="20" x2="12" y2="4" />
                    <line x1="6" y1="20" x2="6" y2="14" />
                  </svg>
                </button>
              </div>
              <v-select
                :value="answerForQuestion(q.value) || null"
                @input="(val) => onGroupQuestionAnswer(q.value, val)"
                :options="getAnswerOptionsForGroup(q.value)"
                :reduce="(opt) => opt.value"
                :clearable="true"
                placeholder="Filtrar por respuesta..."
                label="label"
                class="chart-filter-panel__select"
                append-to-body
                :calculate-position="withPopper"
              />
            </div>
          </div>

          <div
            v-if="answeredCount > 0 || selectedQuestions.length > 0"
            class="chart-filter-panel__action-row"
          >
            <button
              v-if="answeredCount > 0"
              type="button"
              class="chart-filter-panel__analysis-clear"
              @click="$emit('clear-process-form-pairs')"
            >
              Limpiar filtros
            </button>
            <button
              v-if="selectedQuestions.length > 0"
              type="button"
              class="chart-filter-panel__analysis-clear"
              @click="$emit('clear-selected-questions')"
            >
              Limpiar gráfica ({{ selectedQuestions.length }})
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { ref, computed, watch } from "@vue/composition-api";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import popperHelper from "@/libs/popper-helper";
import { getDatasetModule } from "../config/datasets";

export default {
  name: "ChartFilterPanel",

  components: { vSelect, flatPickr },

  props: {
    filterKeys: { type: Array, default: () => [] },
    activeFilters: { type: Object, default: () => ({}) },
    filterOptionsMap: { type: Object, default: () => ({}) },
    getOptionsForFilter: { type: Function, required: true },
    // Filter keys that must render read-only (permission-restricted access)
    lockedFilters: { type: Array, default: () => [] },
    translateFilter: { type: Function, required: true },
    // Process-form props
    isProcessFormField: { type: Boolean, default: false },
    selectedDataset: { type: String, default: "" },
    activeProcessFormLabel: { type: String, default: "" },
    processFormPairs: { type: Array, default: () => [{ question: "", answer: "" }] },
    isMultiQuestionnaire: { type: Boolean, default: false },
    questionnaireOptions: { type: Array, default: () => [] },
    selectedQuestions: { type: Array, default: () => [] },
  },

  setup(props, { emit }) {
    // Show the question+answer panel whenever a processForm field is selected
    const showQuestionPanel = computed(() => props.isProcessFormField);

    // Always hide 'question' from the main filter list (it lives in the panel)
    const visibleFilterKeys = computed(() => {
      return props.filterKeys.filter((k) => k !== "question");
    });

    // ─── Build one group per questionnaire (process-form filter) ──────
    const questionnaireGroups = computed(() => {
      if (!props.isProcessFormField) return [];

      const module = getDatasetModule(props.selectedDataset);
      if (!module?.filters) return [];

      return module.filters
        .filter((f) => f.filterType === "process-form")
        .map((f) => {
          const label = f.forField || f.label;
          const isDynamic = f.optionsConfig?.type === "json-keys";

          let questions;
          if (isDynamic) {
            const entry = props.filterOptionsMap["question"];
            questions = (entry?.options || []).map((q) => ({
              value: q.value,
              label: q.value,
            }));
          } else {
            questions = (f.optionsConfig?.options || [])
              .filter((q) => {
                if (!q.options || q.options.length === 0) return false;
                const t = (q.type || "").toLowerCase();
                if (t === "files_section") return false;
                if (
                  q.options.length === 1 &&
                  ["comentarios", "comentario"].includes(q.options[0].toLowerCase())
                )
                  return false;
                return true;
              })
              .map((q) => ({ value: q.value, label: q.value }));
          }

          const loading =
            isDynamic && props.filterOptionsMap["question"]?.loading === true;
          return { label, questions, isDynamic, loading };
        })
        .filter((g) => g.isDynamic || g.questions.length > 0);
    });

    // ─── Active group (always the first — no tab selection needed) ──
    const activeGroup = computed(() => questionnaireGroups.value[0] || null);

    // Auto-trigger options load when a dynamic group becomes available
    watch(
      questionnaireGroups,
      (groups) => {
        const first = groups[0];
        if (first?.isDynamic) {
          emit("questionnaire-change", first.label);
        }
      },
      { immediate: true }
    );

    // ─── Answer helpers ─────────────────────────────────────────────
    const answerForQuestion = (questionValue) => {
      const match = props.processFormPairs.find((p) => p.question === questionValue);
      return match?.answer || "";
    };

    const answeredCount = computed(
      () => props.processFormPairs.filter((p) => p.question && p.answer).length
    );

    // ─── Answer options from the active json-keys group ─────────────
    const getAnswerOptionsForGroup = (questionValue) => {
      if (!questionValue || !props.isProcessFormField) return [];
      const entry = props.filterOptionsMap["question"];
      const answers = entry?.questionsMap?.[questionValue] || [];
      return answers.map((a) => ({ value: a, label: a }));
    };

    // ─── Emit answer ────────────────────────────────────────────────
    const onGroupQuestionAnswer = (questionValue, val) => {
      if (activeGroup.value?.isDynamic) {
        emit("questionnaire-change", activeGroup.value.label);
      }
      emit("set-process-form-answer", { question: questionValue, answer: val || "" });
    };

    const isQuestionSelected = (questionValue) =>
      props.selectedQuestions.includes(questionValue);

    return {
      showQuestionPanel,
      visibleFilterKeys,
      questionnaireGroups,
      activeGroup,
      answerForQuestion,
      answeredCount,
      getAnswerOptionsForGroup,
      onGroupQuestionAnswer,
      isQuestionSelected,
      withPopper: popperHelper.withPopper,
      flatpickrConfig: {
        mode: "range",
        dateFormat: "Y-m-d",
        allowInput: false,
      },
    };
  },

  methods: {
    getFilterType(filterKey) {
      const entry = this.filterOptionsMap[filterKey];
      return entry ? entry.type : "freetext";
    },

    isFilterLocked(filterKey) {
      return this.lockedFilters.includes(filterKey);
    },

    getDisplayOptions(filterKey) {
      return this.getOptionsForFilter(filterKey);
    },

    isFilterLoading(filterKey) {
      const entry = this.filterOptionsMap[filterKey];
      return entry ? entry.loading : false;
    },

    getReducedValue(filterKey, opt) {
      const entry = this.filterOptionsMap[filterKey];
      if (!entry) return opt.value;
      return entry.valueField === "label" ? opt.label : opt.value;
    },

    /**
     * Parse the comma-separated string back to an array of values (e.g. IDs)
     * for v-select multiple. v-select will use the 'reduce' prop to find
     * the corresponding labels in the options array.
     */
    parseFilterValue(filterKey) {
      const val = this.activeFilters[filterKey];
      if (!val) return [];
      return Array.isArray(val) ? val : String(val).split(",").filter(Boolean);
    },

    onSelectInput(filterKey, values) {
      if (Array.isArray(values) && values.length > 0) {
        this.$emit("filter-change", { key: filterKey, value: values.join(",") });
      } else {
        this.$emit("filter-change", { key: filterKey, value: null });
      }
    },
  },
};
</script>

<style scoped>
.chart-filter-panel {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-start;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.chart-filter-panel__item {
  flex: 1 1 250px;
  min-width: 160px;
  max-width: 280px;
}

.chart-filter-panel__label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 4px;
}

.chart-filter-panel__select {
  font-size: 0.85rem;
}

.chart-filter-panel__datepicker-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

/* Leave look-and-feel to .form-control / dark-layout tokens; only tweak
   right-padding to make room for the clear button. */
.chart-filter-panel__datepicker {
  padding-right: 28px;
  height: 2.5rem;
}

.chart-filter-panel__datepicker-clear {
  position: absolute;
  right: 8px;
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  font-size: 1.1rem;
  line-height: 1;
  padding: 0;
  height: 22px;
}

.chart-filter-panel__datepicker-clear:hover {
  opacity: 1;
}

.chart-filter-panel__datepicker-clear:hover {
  opacity: 1;
}

/* ═══ Analysis Panel ═══ */
.chart-filter-panel__analysis {
  width: 100%;
  flex-basis: 100%;
}

.chart-filter-panel__analysis-body {
  display: grid;
  gap: 12px;
  margin-top: 10px;
  padding: 12px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  max-height: 360px;
  overflow-y: auto;
}

.chart-filter-panel__analysis-body > .chart-filter-panel__analysis-item {
  grid-column: 1 / -1;
  min-width: 0;
}

.chart-filter-panel__analysis-item .chart-filter-panel__label {
  font-size: 0.7rem;
  text-transform: none;
  letter-spacing: normal;
  opacity: 0.85;
}

/* ═══ Questions Panel ═══ */
.chart-filter-panel__tab-loading {
  font-size: 0.8rem;
  opacity: 0.6;
  letter-spacing: 2px;
  padding: 4px 0;
}

.chart-filter-panel__questions-body {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-start;
  margin-top: 8px;
}

.chart-filter-panel__label--question {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
  line-height: 1.3;
}

.chart-filter-panel__action-row {
  grid-column: 1 / -1;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.chart-filter-panel__analysis-clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 5px 12px;
  font-size: 0.75rem;
  border: 1px solid rgba(255, 100, 100, 0.25);
  border-radius: 6px;
  background: rgba(255, 100, 100, 0.08);
  color: #ff8888;
  cursor: pointer;
  transition: all 0.15s;
}

.chart-filter-panel__analysis-clear:hover {
  background: rgba(255, 100, 100, 0.2);
  border-color: rgba(255, 100, 100, 0.4);
}

.chart-filter-panel__question-header {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  margin-bottom: 4px;
}

.chart-filter-panel__question-header .chart-filter-panel__label {
  margin-bottom: 0;
  flex: 1;
}

.chart-filter-panel__graph-btn {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 4px;
  border: 1px solid rgba(128, 128, 128, 0.2);
  background: rgba(128, 128, 128, 0.05);
  color: inherit;
  cursor: pointer;
  opacity: 0.5;
  transition: all 0.15s;
  padding: 0;
}

.chart-filter-panel__graph-btn:hover {
  opacity: 1;
  background: rgba(128, 128, 128, 0.12);
}

.chart-filter-panel__graph-btn.is-active {
  opacity: 1;
  background: rgba(var(--primary-rgb, 27, 61, 94), 0.15);
  border-color: var(--primary, #1b3d5e);
  color: var(--primary, #1b3d5e);
}

.dark-layout .chart-filter-panel__graph-btn.is-active {
  background: rgba(var(--info-rgb, 62, 140, 199), 0.2);
  border-color: var(--info, #3e8cc7);
  color: var(--info, #3e8cc7);
}

/* Slide transition */
.chart-filter-slide-enter-active,
.chart-filter-slide-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}
.chart-filter-slide-enter,
.chart-filter-slide-leave-to {
  opacity: 0;
  max-height: 0;
  margin-top: 0;
  padding-top: 0;
}
</style>
